import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plane, Loader2 } from "lucide-react";

// §12.8: "I'm out until <date>, my backup is <person>". Self, or an admin for anyone (targetId).
export default function OutOfOfficeDialog({ open, onClose, targetId, targetName, current, onSaved }) {
  const [users, setUsers] = useState([]);
  const [until, setUntil] = useState("");
  const [backup, setBackup] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const me = !targetId;
  const base = me ? "/me/out-of-office" : `/users/${targetId}/out-of-office`;

  useEffect(() => {
    if (!open) return;
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.is_active !== false))).catch(() => setUsers([]));
    setUntil(current?.until || ""); setBackup(current?.backup_user_id || ""); setNote(current?.note || "");
  }, [open, current]);

  const save = async () => {
    if (!until || !backup) { toast.error("Pick a return date and a backup"); return; }
    setBusy(true);
    try { const r = await api.put(base, { until, backup_user_id: backup, note }); toast.success(`Out of office set — back ${new Date(until + "T12:00:00").toLocaleDateString()}`); onSaved && onSaved(r.data.out_of_office); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.delete(base); toast.success("Welcome back — out of office cleared"); onSaved && onSaved(null); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not clear"); }
    finally { setBusy(false); }
  };
  const self = users.find((u) => u.user_id === (targetId || current?.user_id));
  const options = users.filter((u) => u.user_id !== targetId && (me ? u.user_id !== (self?.user_id) : true));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ooo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plane className="w-4 h-4 text-[#003E87]" /> {me ? "Out of office" : `Out of office — ${targetName || "teammate"}`}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">While {me ? "you are" : "they are"} out, sign-offs, the 7 AM reminders and new bids that would land on {me ? "you" : "them"} go to the backup. It clears itself the day after the return date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Back on</Label>
            <Input type="date" data-testid="ooo-until" className="mt-1" value={until} onChange={(e) => setUntil(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Backup</Label>
            <Select value={backup} onValueChange={setBackup}>
              <SelectTrigger data-testid="ooo-backup" className="mt-1"><SelectValue placeholder="Who covers?" /></SelectTrigger>
              <SelectContent>{options.map((u) => <SelectItem key={u.user_id} value={u.user_id} data-testid={`ooo-backup-${u.user_id}`}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Note <span className="text-slate-400">(optional)</span></Label>
            <Input data-testid="ooo-note" className="mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. PTO, reachable by text for emergencies" />
          </div>
          <div className="flex justify-between gap-2 pt-1">
            {current?.until ? <Button variant="ghost" onClick={clear} disabled={busy} data-testid="ooo-clear-btn" className="text-rose-600 hover:text-rose-700">I&rsquo;m back — clear</Button> : <span />}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button data-testid="ooo-save-btn" disabled={busy} onClick={save} className="bg-[#003E87] hover:bg-[#00305F]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set out of office"}</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OooBadge({ ooo, testid }) {
  if (!ooo?.active) return null;
  return (
    <span data-testid={testid} title={ooo.note || ""} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
      <Plane className="w-3 h-3" /> Out · back {new Date(ooo.until + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}{ooo.backup_name ? ` · ${ooo.backup_name} covers` : ""}
    </span>
  );
}
